import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { environment } from '@fantaz/shared/app-config';
import {
  GAEventKeys,
  GoogleAnalyticsEvent,
  Location,
} from '@fantaz/shared/data-access/models';
import { FzGoogleTagManagerService } from './fz-googleTag-manager.service';
// !FIXME: remove circular dependency
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import { Observable, catchError, exhaustMap, from, map, of } from 'rxjs';
import { ConfigApiService } from './config-api.service';

export interface LocationResponse {
  deviceGPS?: Location | null;
  userAllowToUseFallbackLocation?: boolean;
}

declare const window: any;
@Injectable({
  providedIn: 'root',
})
export class CaptureLocationService {
  force = false;
  geo_options = {
    enableHighAccuracy: true,
    maximumAge: 300000,
    timeout: 10000,
  };
  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private gtmService: FzGoogleTagManagerService,
    private configApiService: ConfigApiService,
  ) {
    this.init();
  }

  init() {
    this.force = localStorage.getItem('location-force-shared')
      ? !!localStorage.getItem('location-force-shared')
      : false;
  }

  setForce(value: boolean) {
    this.sendGAEventLocation(
      GAEventKeys.USER_LOCATION_SHARED,
      'forced_by_user',
    );
    localStorage.setItem('location-force-shared', value + '');
    this.force = value || false;
  }

  getPermission(): Observable<any> {
    if (
      window.navigator.permissions &&
      typeof window.navigator.permissions.query == 'function'
    ) {
      return from(
        window.navigator.permissions.query({ name: 'geolocation' }),
      ).pipe(
        map((permissionStatus) => {
          return permissionStatus;
        }),
        catchError((err) => {
          return of(err);
        }),
      );
    } else {
      return of('Incompatible browser');
    }
  }

  getLocation(): Observable<any> {
    return from(this.getLocationPromise());
  }
  getLocationPromise(): Promise<any> {
    const geo_options = {
      enableHighAccuracy: true,
      maximumAge: 300000,
      timeout: 10000,
    };

    const geolocationTimer = setTimeout(() => {
      return new Promise((resolve, reject) => {
        const message = 'Unable to capture location';

        if (this.force) {
          resolve({
            location: null,
            userAllowToUseFallbackLocation: true,
          });
        } else {
          this.sendGAEventLocation(
            GAEventKeys.USER_LOCATION_INACCESSIBLE,
            'location_denied_by_system',
          );
          reject(message);
        }
      });
    }, 12000);

    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position: any) => {
            if (geolocationTimer) {
              clearTimeout(geolocationTimer);
            }
            resolve({
              Latitude: position.coords.latitude,
              Longitude: position.coords.longitude,
              Altitude: position.coords.altitude,
              Speed: position.coords.speed,
              DateTime: position.timestamp,
            });
          },
          (err) => {
            if (geolocationTimer) {
              clearTimeout(geolocationTimer);
            }
            const force = localStorage.getItem('location-force-shared');
            if (force !== 'true') {
              let logMessage = '';
              console.warn(err);
              if (err.code === 1) {
                logMessage =
                  'In order to play cash games, FantaZ needs access to your location. Please update your browser settings to allow FantaZ to check your location.';
                console.error(logMessage);
                this.sendGAEventLocation(
                  GAEventKeys.USER_LOCATION_SHARED_FAIL,
                  'location_denied',
                );
              } else if (err.code === 2) {
                logMessage =
                  'Please check that you have location services enabled on your device, or try back later.';
                console.error(logMessage);
                this.sendGAEventLocation(
                  GAEventKeys.USER_LOCATION_SHARED_FAIL,
                  'location_denied',
                );
              } else {
                console.error('Unknown error getting Geolocation');
              }
              reject(err);
            } else {
              this.sendGAEventLocation(
                GAEventKeys.USER_LOCATION_FALLBACK_BY_IP,
                'location_fallback_by_ip',
              );
              reject({ message: err.message, code: err.code });
            }
          },
          geo_options,
        );
      } else {
        reject({ message: 'Unsupported browser', code: 3 });
      }
    });
  }

  verifyCashPlayStatus(options: {
    deviceGPS?: Location | null | undefined;
    userAllowToUseFallbackLocation?: boolean;
  }): Observable<{ blocked: boolean }> {
    if (!this.configApiService.isTSEVODisabled()) {
      return this.http
        .post<{ blocked: boolean }>(
          `${environment.apiBaseUrl}/core-client/api/user/verifyLocation`,
          {
            userLocation: {
              deviceGPS: options.deviceGPS,
              userAllowToUseFallbackLocation:
                options.userAllowToUseFallbackLocation,
            },
          },
        )
        .pipe(
          map((data) => {
            if (data && data.blocked === true) {
              this.sendGAEventLocation(
                GAEventKeys.USER_LOCATION_BLOCKED,
                'unauthorized_location',
              );
            }
            return data;
          }),
        );
    }
    console.warn('endpoint "/core-client/api/user/verifyLocation" mocked');
    return of({ blocked: false });
  }

  async sendGAEventLocation(eventName: GAEventKeys, type: string) {
    const sessionString = localStorage.getItem('user_session');
    if (sessionString) {
      const session = JSON.parse(sessionString);
      const {
        idToken: { payload },
      } = session;
      const event = {
        event: 'user_location_status',
        location_action: eventName,
        user_id: payload.sub,
        user_name: localStorage.getItem('username'),
        location_details: type,
        timestamp: new Date().getTime(),
      } as GoogleAnalyticsEvent;

      await this.gtmService.pushTag(event);
    }
  }

  openErrorDialog(errorType = 'location-disabled') {
    const ref = this.dialog.open(ModalAlertComponent, {
      data: {
        modalType: errorType,
      },
    });

    ref.afterClosed().subscribe((result) => {
      this.setForce(true);
      this.getLocation();
    });
    return ref;
  }

  // TODO: Move for core module
  /**
   * @deprecated use import { LocationService } from `@fantaz/core/utils`
   */
  observeLocation(): Observable<LocationResponse> {
    const obs = this.getPermission().pipe(
      map((res) => {
        return res;
      }),
      exhaustMap((res) => {
        if (res && (res.state === 'granted' || res.state === 'prompt')) {
          // get location from browser
          return this.getLocation().pipe(
            map((location) => {
              return {
                deviceGPS: location,
              };
            }),
          );
        }
        if (this.force) {
          return of({
            userAllowToUseFallbackLocation: true,
          });
        }

        // request user permission
        const dialogRef = this.dialog.open(ModalAlertComponent, {
          data: {
            modalType: 'location-disabled',
            required: true,
          },
          disableClose: true,
          restoreFocus: true,
        });

        return dialogRef.afterClosed().pipe(
          map((result: boolean) => {
            // store user allowed permission
            this.setForce(result);

            // return permission result
            return {
              userAllowToUseFallbackLocation: result,
            };
          }),
        );
      }),
      map((result) => {
        return result;
      }),
    );

    return obs;
  }
}
